import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getSocket } from '../../socket';
import { toast } from 'react-toastify';

const toSeconds = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [minutes, seconds] = value.split(':').map(Number);
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) return null;
  return (minutes * 60) + seconds;
};

const formatTime = (value) => `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;

export default forwardRef(function AgendaItemTimerControls({ item, meeting, getMeeting, isTimerManager, showClockText = true, isLocked = false, readOnly = false, segmentId = '', liveState = null, onStart, onStop, onTimerStateChange }, ref) {
  const url = process.env.REACT_APP_URL;
  const { user: authUser } = useSelector((state) => state.auth);
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id || meeting?._id || item?.meeting;
  const agendaItemId = String(item?.agendaItemId || item?._id || item?.id || '').trim();
  const [time, setTime] = useState(Number(item?.timer?.time || 0));
  const [isPlaying, setIsPlaying] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTime(Number(item?.timer?.time || 0));
  }, [item?.agendaItemId, item?.timer?.time, segmentId]);

  useEffect(() => {
    if (!liveState) return;
    if (Number.isFinite(Number(liveState.time))) setTime(Math.max(0, Number(liveState.time)));
    if (typeof liveState.isPlaying === 'boolean') setIsPlaying(liveState.isPlaying);
  }, [liveState]);

  useEffect(() => {
    if ((!isTimerManager && !readOnly) || !meetingId || !agendaItemId) return undefined;
    const socket = getSocket();
    const meetingRoomId = String(meetingId);
    socket.emit('meeting:join', meetingRoomId);

    const handleTick = (payload) => {
      if (String(payload?.segmentId || '') !== segmentId || String(payload?.meetingId || '') !== meetingRoomId || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
    };
    const handleReset = (payload) => {
      const resetMeetingId = payload?.meetingId || payload?.detail?.meetingId || meetingRoomId;
      const resetAgendaItemId = payload?.agendaItemId || payload?.detail?.agendaItemId;
      if (String(payload?.segmentId || payload?.detail?.segmentId || '') !== segmentId) return;
      if (String(resetMeetingId) === meetingRoomId && String(resetAgendaItemId || '') === agendaItemId) {
        setIsPlaying(false);
        setTime(0);
      }
    };
    const handleTimerState = (payload) => {
      if (String(payload?.segmentId || '') !== segmentId || String(payload?.meetingId || '') !== meetingRoomId || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
      if (typeof payload?.isPlaying === 'boolean') setIsPlaying(payload.isPlaying);
    };

    socket.on('meeting:timer-tick', handleTick);
    socket.on('meeting:timer-state', handleTimerState);
    socket.on('meeting:timer-reset', handleReset);
    window.addEventListener('meeting:timer-reset', handleReset);
    socket.emit('meeting:join', meetingRoomId);

    return () => {
      socket.off('meeting:timer-tick', handleTick);
      socket.off('meeting:timer-state', handleTimerState);
      socket.off('meeting:timer-reset', handleReset);
      window.removeEventListener('meeting:timer-reset', handleReset);
    };
  }, [agendaItemId, isTimerManager, meetingId, readOnly, segmentId]);

  // Ticks arrive from the server (single source of truth), so no local interval
  // increments/emits here — that would double-count seconds across browsers.

  const timerProgress = useMemo(() => {
    const maximum = toSeconds(item?.agendaEvent?.red);
    return maximum > 0 ? Math.min(100, Math.max(0, (time / maximum) * 100)) : 0;
  }, [item?.agendaEvent?.red, time]);

  const timerRingBackground = useMemo(() => {
    const green = toSeconds(item?.agendaEvent?.green);
    const amber = toSeconds(item?.agendaEvent?.amber);
    const red = toSeconds(item?.agendaEvent?.red);
    if (!red || red <= 0 || timerProgress <= 0) return 'conic-gradient(from 0deg, rgba(255, 255, 255, 0.16) 0% 100%)';
    if (timerProgress >= 100) return 'conic-gradient(from 0deg, #dc3545 0% 100%)';

    const greenProgress = Math.min(100, Math.max(0, (green || 0) / red * 100));
    const amberProgress = Math.min(100, Math.max(greenProgress, (amber || green || 0) / red * 100));
    const segments = [
      ['#56d3ff', 0, Math.min(greenProgress, timerProgress)],
      ['#146c43', greenProgress, Math.min(amberProgress, timerProgress)],
      ['#ffc107', amberProgress, Math.min(100, timerProgress)],
      ['#dc3545', 100, timerProgress],
    ].filter(([, start, end]) => end > start && start < timerProgress);
    const stops = segments.flatMap(([color, start, end]) => [`${color} ${start}%`, `${color} ${end}%`]);
    stops.push(`rgba(255, 255, 255, 0.16) ${timerProgress}%`, 'rgba(255, 255, 255, 0.16) 100%');
    return `conic-gradient(from 0deg, ${stops.join(', ')})`;
  }, [item?.agendaEvent, timerProgress]);

  const timerColor = timerProgress >= 100 ? '#dc3545'
    : time >= (toSeconds(item?.agendaEvent?.red) || Infinity) ? '#dc3545'
      : time >= (toSeconds(item?.agendaEvent?.amber) || Infinity) ? '#ffc107'
        : time >= (toSeconds(item?.agendaEvent?.green) || Infinity) ? '#146c43'
          : 'var(--modal-bg)';
  const foregroundColor = timerColor === '#ffc107' ? '#000000' : '#ffffff';

  const emitState = playing => {
    const payload = { meetingId, agendaItemId, ...(segmentId ? { segmentId } : {}), isPlaying: playing, time };
    onTimerStateChange?.(payload);
    getSocket().emit('meeting:timer-state', payload);
  };
  const handleStop = async () => {
    if (!isPlaying || !isTimerManager || isLocked || readOnly) return;
      setIsPlaying(false);
      emitState(false);
      setSaving(true);
      try {
        const method = item?.timer ? 'PATCH' : 'PUT';
        const path = `${url}/timer/${agendaItemId}${segmentId ? `/segment/${encodeURIComponent(segmentId)}` : ''}`;
        const [response] = await Promise.all([fetch(path, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser?.token || ''}` },
          body: JSON.stringify({
            event: item?.agendaEvent?.name,
            name: `${item?.assignedUser?.firstName || 'TBA'} ${item?.assignedUser?.lastName || ''}`,
            time,
          }),
        }), onStop?.()]);
        if (!response.ok) throw new Error('Unable to save the agenda timer.');
        await Promise.resolve(getMeeting?.());
      } catch (error) {
        toast.error(error.message, { position: 'bottom-left' });
      } finally {
        setSaving(false);
      }
  };
  const handleStart = async () => {
    if (isPlaying || !isTimerManager || isLocked || readOnly || saving) return;
    setSaving(true);
    try {
      await onStart?.();
      setIsPlaying(true);
      emitState(true);
    } catch (error) {
      toast.error(error.message || 'Unable to start the timer.', { position: 'bottom-left' });
    } finally {
      setSaving(false);
    }
  };
  useImperativeHandle(ref, () => ({ handleStart, handleStop }));

  const handleToggle = event => {
    event.stopPropagation();
    void (isPlaying ? handleStop() : handleStart());
  };

  if ((!isTimerManager && !readOnly) || !agendaItemId || (isLocked && !readOnly)) return null;

  return (
    <div className="agenda-item-timer-controls" onClick={(event) => event.stopPropagation()}>
      <div
        className="agenda-item-timer-circle"
        style={{ background: timerRingBackground, '--agenda-timer-color': timerColor, '--agenda-timer-foreground': foregroundColor }}
        aria-label={`Timer ${formatTime(time)}`}
      >
        <div className="agenda-item-timer-circle-inner">{showClockText ? formatTime(time) : null}</div>
      </div>
      {!readOnly && <Button
        className={`agenda-item-timer-button ${isPlaying ? 'agenda-item-timer-button--pause' : 'agenda-item-timer-button--start'}`}
        variant="link"
        onClick={handleToggle}
        disabled={saving}
        title={isPlaying ? (segmentId ? 'Stop speaker timer' : 'Pause timer') : (segmentId ? 'Start speaker timer' : 'Start timer')}
        aria-label={isPlaying ? (segmentId ? 'Stop speaker timer' : 'Pause timer') : (segmentId ? 'Start speaker timer' : 'Start timer')}
      >
        <i className={`bi ${isPlaying ? (segmentId ? 'bi-stop-fill' : 'bi-pause-fill') : 'bi-play-fill'} agenda-item-timer-button-icon`} aria-hidden="true" />
      </Button>}
    </div>
  );
});
